import { useEffect, useRef, useState } from 'react';
import { Upload, Loader2, X, Eye, RotateCcw, AlertCircle, FileText, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { AuthedImage, viewFile } from './AuthedImage';

// PR-UPLOAD-FIX MAY 11 — reusable upload slot used by every screen that
// takes a file (Istemarah, flatbed photos, water trip photos, driver docs,
// supplier topup receipt). Three things this gives that the bespoke slots
// don't:
//
//   1. **Stage-then-commit.** Picking/pasting/dropping a file shows a local
//      preview with Remove + Upload buttons. Nothing hits the server until
//      the user clicks Upload — addresses Ayan's "I can't remove a wrongly
//      picked file before it goes up" complaint. Once committed the
//      preview switches to the server file and the Remove button is gone
//      (per his rule: removable until upload, immutable after).
//
//   2. **Paste-to-upload.** Cmd/Ctrl-V inside the slot pastes a clipboard
//      image (e.g. screenshot) directly into the staging area. No more
//      "download, rename, re-upload" dance.
//
//   3. **Drag & drop.** Dropping a file onto the slot stages it.
//
// The component is purely UI — the caller passes onUpload(file) and gets
// back a promise. Networking lives in the caller's mutation.

export interface PasteableUploadProps {
  /** Server hands the staged File back; resolve on success, reject on failure. */
  onUpload: (file: File) => Promise<void>;
  /** Already-uploaded file (server-relative URL). Switches the slot to the "filled" state. */
  currentFileUrl?: string | null;
  /** Mime hint for the filled state — controls thumbnail vs. PDF tile. */
  currentFileType?: string | null;
  /** Click "Replace" on a filled slot resets to empty so a new file can be staged. */
  onReplace?: () => void;
  /** input[type=file] accept attribute. Defaults to images + pdf. */
  accept?: string;
  /** Bytes; default 10 MB. */
  maxSizeMB?: number;
  /** Heading shown above the slot. */
  label?: React.ReactNode;
  /** Sub-label inside the empty state. */
  helperText?: string;
  /** Render a small chip in the corner of the empty state (e.g. "required"). */
  badge?: React.ReactNode;
  /** Mirrors PhotoSlot's `highlight` — pulsing rose border when slot is required + empty. */
  highlight?: boolean;
  /** Extra wrapper class. */
  className?: string;
  /** Disable interaction. */
  disabled?: boolean;
  /** Show the "View full" button on filled state. Default true. */
  showViewFull?: boolean;
}

type Stage = { file: File; preview: string };

export function PasteableUpload({
  onUpload,
  currentFileUrl,
  currentFileType,
  onReplace,
  accept = 'image/*,application/pdf',
  maxSizeMB = 10,
  label,
  helperText,
  badge,
  highlight,
  className,
  disabled,
  showViewFull = true,
}: PasteableUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [staged, setStaged] = useState<Stage | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clean up preview blob URLs as the staged file changes / unmounts.
  useEffect(() => () => { if (staged) URL.revokeObjectURL(staged.preview); }, [staged]);

  function stageFile(f: File) {
    setError(null);
    if (f.size > maxSizeMB * 1024 * 1024) {
      setError(`File too large (max ${maxSizeMB} MB)`);
      return;
    }
    // Best-effort accept check on extension/mime.
    if (accept && !matchesAccept(f, accept)) {
      setError(`File type not allowed (${f.type || 'unknown'})`);
      return;
    }
    if (staged) URL.revokeObjectURL(staged.preview);
    setStaged({ file: f, preview: URL.createObjectURL(f) });
  }

  function clearStaged() {
    if (staged) URL.revokeObjectURL(staged.preview);
    setStaged(null);
    setError(null);
  }

  async function commit() {
    if (!staged) return;
    setUploading(true);
    setError(null);
    try {
      await onUpload(staged.file);
      // On success the parent reloads and passes a fresh currentFileUrl;
      // clear the staging state so the filled view takes over.
      clearStaged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  // Paste — listen on the container so it only catches when the user is
  // actually interacting with this slot.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    function onPaste(e: ClipboardEvent) {
      if (disabled || uploading) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind === 'file') {
          const blob = item.getAsFile();
          if (blob) {
            e.preventDefault();
            const ext = (blob.type.split('/')[1] || 'png').replace('+xml', '');
            const named = new File([blob], `pasted-${Date.now()}.${ext}`, { type: blob.type });
            stageFile(named);
            return;
          }
        }
      }
    }
    el.addEventListener('paste', onPaste as EventListener);
    return () => el.removeEventListener('paste', onPaste as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, uploading, staged]);

  const empty = !currentFileUrl && !staged;
  const showFilled = !!currentFileUrl && !staged;

  const borderCls = empty
    ? (highlight ? 'border-rose-500 animate-pulse' : 'border-amber-500/40')
    : 'border-border';

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onDragOver={(e) => { if (disabled || uploading) return; e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        if (disabled || uploading) return;
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) stageFile(f);
      }}
      className={cn(
        'rounded-lg border-2 border-dashed p-3 space-y-2 bg-card transition-colors outline-none',
        empty && 'border-dashed',
        borderCls,
        dragOver && 'border-primary bg-primary/5',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
    >
      {(label || badge) && (
        <div className="flex items-start justify-between gap-2">
          {label && <div className="text-sm font-medium">{label}</div>}
          {badge}
        </div>
      )}

      {/* --- empty state --- */}
      {empty && (
        <label className="flex flex-col items-center justify-center h-24 rounded-md bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors gap-1 text-center px-3">
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            disabled={disabled || uploading}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) stageFile(f); e.currentTarget.value = ''; }}
          />
          <Upload className="h-4 w-4 text-muted-foreground" />
          <div className="text-xs text-muted-foreground leading-tight">
            {helperText ?? 'Click, drop, or paste (Cmd+V)'}
          </div>
        </label>
      )}

      {/* --- staged (pre-upload) state --- */}
      {staged && (
        <div className="space-y-2">
          <div className="h-32 rounded-md bg-muted/30 border border-border overflow-hidden flex items-center justify-center">
            {staged.file.type.startsWith('image/') ? (
              <img src={staged.preview} alt={staged.file.name} className="max-h-full max-w-full object-contain" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <FileText className="h-8 w-8" />
                <span className="text-xs px-2 truncate max-w-[12rem]">{staged.file.name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={commit} disabled={uploading} className="gap-1">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={clearStaged} disabled={uploading} className="gap-1">
              <X className="h-3.5 w-3.5" /> Remove
            </Button>
            <span className="text-[11px] text-muted-foreground truncate" title={staged.file.name}>
              {staged.file.name} · {formatBytes(staged.file.size)}
            </span>
          </div>
        </div>
      )}

      {/* --- filled (already uploaded) state --- */}
      {showFilled && currentFileUrl && (
        <div className="space-y-2">
          <div className="h-32 rounded-md bg-muted/30 border border-border overflow-hidden flex items-center justify-center">
            {currentFileType === 'application/pdf' ? (
              <div className="flex flex-col items-center gap-1 text-muted-foreground">
                <FileText className="h-8 w-8" />
                <span className="text-xs">PDF · click View to open</span>
              </div>
            ) : (
              <AuthedImage
                src={currentFileUrl}
                alt={typeof label === 'string' ? label : 'Uploaded file'}
                className="max-h-full max-w-full object-contain"
                emptyFallback={
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <ImageIcon className="h-8 w-8" />
                    <span className="text-xs">Image uploaded</span>
                  </div>
                }
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            {showViewFull && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={() => viewFile(currentFileUrl).catch((e) => setError(e instanceof Error ? e.message : 'Open failed'))}
              >
                <Eye className="h-3.5 w-3.5" /> View full
              </Button>
            )}
            {onReplace && (
              <Button type="button" size="sm" variant="ghost" className="gap-1" onClick={onReplace}>
                <RotateCcw className="h-3.5 w-3.5" /> Replace
              </Button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-1 text-xs text-rose-700 dark:text-rose-400">
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function matchesAccept(file: File, accept: string): boolean {
  const patterns = accept.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
  if (patterns.length === 0) return true;
  const type = (file.type || '').toLowerCase();
  const name = file.name.toLowerCase();
  return patterns.some((p) => {
    if (p.startsWith('.')) return name.endsWith(p);
    if (p.endsWith('/*')) return type.startsWith(p.slice(0, -1)); // e.g. "image/"
    return type === p;
  });
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
